import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CreditCard,
  DollarSign,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Clock,
  Banknote,
  Receipt,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface UploadFee {
  id: string;
  fee_type: string;
  amount_usd: number;
  paid: boolean;
  paid_at: string | null;
  created_at: string;
  work_id: string | null;
}

const Billing = () => {
  const { publisher, refreshPublisher } = useAuth();
  const [connectLoading, setConnectLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [uploadFees, setUploadFees] = useState<UploadFee[]>([]);
  const [feesLoading, setFeesLoading] = useState(true);
  const [workTitles, setWorkTitles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (publisher?.id) {
      fetchUploadFees();
    }
  }, [publisher?.id]);

  const fetchUploadFees = async () => {
    if (!publisher?.id) return;
    setFeesLoading(true);
    try {
      const { data, error } = await supabase
        .from('content_upload_fees')
        .select('id, fee_type, amount_usd, paid, paid_at, created_at, work_id')
        .eq('publisher_id', publisher.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const fees = (data || []) as unknown as UploadFee[];
      setUploadFees(fees);

      const workIds = fees.filter(f => f.work_id).map(f => f.work_id as string);
      if (workIds.length > 0) {
        const { data: works } = await supabase
          .from('works')
          .select('id, title')
          .in('id', workIds);
        if (works) {
          const titleMap: Record<string, string> = {};
          for (const w of works) titleMap[w.id] = w.title;
          setWorkTitles(titleMap);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch upload fees:', err);
    } finally {
      setFeesLoading(false);
    }
  };

  // Derive Stripe Connect status from publisher record (already loaded by AuthContext)
  const getConnectStatus = (): 'active' | 'pending' | 'incomplete' | 'not_connected' => {
    if (!publisher?.stripe_connect_id) return 'not_connected';
    if (publisher.stripe_connected) return 'active';
    return 'incomplete'; // Connect ID exists but onboarding not complete
  };

  const connectStatus = getConnectStatus();

  const handleConnectStripe = async () => {
    if (!publisher?.id) return;
    setConnectLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-connect-account-link', {
        body: { publisher_id: publisher.id, action: 'onboard' },
      });
      if (error) throw error;
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to start Stripe Connect onboarding');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleViewStripeDashboard = async () => {
    if (!publisher?.id) return;
    setDashboardLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-connect-account-link', {
        body: { publisher_id: publisher.id, action: 'dashboard' },
      });
      if (error) throw error;
      if (data?.dashboard_url) {
        window.open(data.dashboard_url, '_blank');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to open Stripe Dashboard');
    } finally {
      setDashboardLoading(false);
      // Refresh publisher data in case onboarding completed in the background
      await refreshPublisher();
    }
  };

  const getStatusBadge = () => {
    switch (connectStatus) {
      case 'active':
        return (
          <Badge className='bg-green-500/10 text-green-500 border-green-500/30'>
            <CheckCircle2 className='h-3 w-3 mr-1' />
            Active
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant='outline' className='text-yellow-500 border-yellow-500/30'>
            <Clock className='h-3 w-3 mr-1' />
            Pending
          </Badge>
        );
      case 'incomplete':
        return (
          <Badge variant='outline' className='text-orange-500 border-orange-500/30'>
            <AlertCircle className='h-3 w-3 mr-1' />
            Setup Incomplete
          </Badge>
        );
      default:
        return <Badge variant='secondary'>Not Connected</Badge>;
    }
  };

  return (
    <div className='p-4 md:p-6 space-y-4 md:space-y-6 text-foreground'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold'>Billing & Payments</h1>
          <p className='text-muted-foreground mt-1 text-sm md:text-base'>
            Manage your Stripe Connect account and payment settings
          </p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Stripe Connect Status */}
      <Card className='partner-card'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <CreditCard className='h-5 w-5' />
            Stripe Connect Status
          </CardTitle>
          <CardDescription>
            Connect your Stripe account to receive payments from Kinora
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {connectStatus === 'not_connected' ? (
            <div className='text-center py-8 space-y-4'>
              <div className='mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center'>
                <Banknote className='h-8 w-8 text-primary' />
              </div>
              <div>
                <h3 className='text-lg font-semibold mb-2'>Connect Your Stripe Account</h3>
                <p className='text-sm text-muted-foreground mb-4 max-w-md mx-auto'>
                  To receive payments from your Kinora works, you'll need to connect a Stripe
                  account. This is a secure, one-time setup process.
                </p>
              </div>
              <Button onClick={handleConnectStripe} size='lg' disabled={connectLoading}>
                <CreditCard className='h-4 w-4 mr-2' />
                {connectLoading ? 'Redirecting...' : 'Connect with Stripe'}
              </Button>
            </div>
          ) : connectStatus === 'incomplete' ? (
            <div className='text-center py-8 space-y-4'>
              <div className='mx-auto w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center'>
                <AlertCircle className='h-8 w-8 text-orange-500' />
              </div>
              <div>
                <h3 className='text-lg font-semibold mb-2'>Setup Incomplete</h3>
                <p className='text-sm text-muted-foreground mb-2 max-w-md mx-auto'>
                  Your Stripe account setup isn't finished yet. Complete onboarding to start
                  receiving payouts.
                </p>
                {publisher?.stripe_connect_id && (
                  <p className='text-xs text-muted-foreground font-mono'>
                    Account: {publisher.stripe_connect_id}
                  </p>
                )}
              </div>
              <Button onClick={handleConnectStripe} disabled={connectLoading}>
                {connectLoading ? 'Redirecting...' : 'Resume Stripe Setup'}
              </Button>
            </div>
          ) : (
            <div className='space-y-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div className='p-4 border border-border/60 rounded-lg bg-muted/30'>
                  <p className='text-sm text-muted-foreground mb-1'>Connect Account ID</p>
                  <p className='font-mono text-sm'>{publisher?.stripe_connect_id}</p>
                </div>
                <div className='p-4 border border-border/60 rounded-lg bg-muted/30'>
                  <p className='text-sm text-muted-foreground mb-1'>Account Status</p>
                  <div className='flex items-center gap-2'>{getStatusBadge()}</div>
                </div>
              </div>

              <div className='flex flex-col sm:flex-row gap-3'>
                <Button
                  variant='outline'
                  onClick={handleViewStripeDashboard}
                  disabled={dashboardLoading}
                  className='flex-1 sm:flex-initial'
                >
                  <ExternalLink className='h-4 w-4 mr-2' />
                  {dashboardLoading ? 'Opening...' : 'View Stripe Dashboard'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Fee History */}
      <Card className='partner-card'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Receipt className='h-5 w-5' />
            Upload Fee History
          </CardTitle>
          <CardDescription>All upload fees charged to your account</CardDescription>
        </CardHeader>
        <CardContent>
          {feesLoading ? (
            <p className='text-sm text-muted-foreground'>Loading...</p>
          ) : uploadFees.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No upload fees yet.</p>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-border/60'>
                    <th className='text-left py-2 pr-4 font-medium text-muted-foreground'>Content</th>
                    <th className='text-left py-2 pr-4 font-medium text-muted-foreground'>Amount</th>
                    <th className='text-left py-2 pr-4 font-medium text-muted-foreground'>Status</th>
                    <th className='text-left py-2 font-medium text-muted-foreground'>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadFees.map((fee) => (
                    <tr key={fee.id} className='border-b border-border/30 last:border-0'>
                      <td className='py-3 pr-4'>
                        {fee.work_id ? (
                          <Link to='/content' className='hover:underline text-primary'>
                            {workTitles[fee.work_id] ?? fee.fee_type.replace(/_/g, ' ')}
                          </Link>
                        ) : (
                          <span className='capitalize'>{fee.fee_type.replace(/_/g, ' ')}</span>
                        )}
                      </td>
                      <td className='py-3 pr-4'>${fee.amount_usd.toFixed(2)}</td>
                      <td className='py-3 pr-4'>
                        {fee.paid ? (
                          <Badge className='bg-green-500/10 text-green-500 border-green-500/30'>
                            <CheckCircle2 className='h-3 w-3 mr-1' />
                            Paid
                          </Badge>
                        ) : (
                          <Badge variant='outline' className='text-yellow-500 border-yellow-500/30'>
                            <Clock className='h-3 w-3 mr-1' />
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className='py-3 text-muted-foreground'>
                        {fee.paid_at
                          ? new Date(fee.paid_at).toLocaleDateString()
                          : new Date(fee.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Information */}
      <Card className='partner-card'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <DollarSign className='h-5 w-5' />
            Payment Information
          </CardTitle>
          <CardDescription>How payments work on Kinora</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4 text-sm'>
            <div className='p-4 border border-border/60 rounded-lg bg-muted/30'>
              <h4 className='font-semibold mb-2'>Payment Schedule</h4>
              <p className='text-muted-foreground'>
                Payments are processed monthly and transferred to your connected Stripe account.
                You'll receive earnings from slices and other revenue sources.
              </p>
            </div>
            <div className='p-4 border border-border/60 rounded-lg bg-muted/30'>
              <h4 className='font-semibold mb-2'>Upload Fees</h4>
              <p className='text-muted-foreground'>
                Movies, short films, and documentaries: <strong>$100</strong> per title. TV
                episodes: <strong>$25–$50</strong> depending on duration. Bonus/behind-the-scenes
                content is free when attached to a paid parent work.
              </p>
            </div>
            <div className='p-4 border border-border/60 rounded-lg bg-muted/30'>
              <h4 className='font-semibold mb-2'>Tax Information</h4>
              <p className='text-muted-foreground'>
                You're responsible for reporting and paying taxes on your Kinora earnings. Stripe
                will provide tax documents through your Stripe Express Dashboard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Billing;
